import { useState, useEffect, useRef, useCallback } from "react";
import ProtonWebSDK from "@proton/web-sdk";
import { CONTRACT, DEFAULT_RPC } from "../constants";
import { serializeError } from "../utils/jsonUtils";
import { postWalletUiCleanup } from "../utils/chainUtils";

export function useWallet() {
  const [link, setLink] = useState(null);
  const [session, setSession] = useState(null);
  const [auth, setAuth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [accessGateMsg, setAccessGateMsg] = useState("");

  const actor = auth?.actor ? String(auth.actor) : "";
  const isContract = actor && actor.toLowerCase() === CONTRACT.toLowerCase();
  const loggedIn = !!auth;

  const lastAuthLoggedActorRef = useRef("");

  const logAuthEvent = useCallback(async (event, actorValue = "", extra = null) => {
    try {
      const pickStr = (...vals) => {
        for (const v of vals) {
          if (typeof v === "string" && v.trim()) return v.trim();
        }
        return "";
      };

      const walletType = pickStr(
        extra?.walletType,
        session?.link?.transport?.walletType,
        session?.link?.transport?.type,
        session?.link?.transport?.name,
        link?.transport?.walletType,
        link?.transport?.type,
        link?.transport?.name
      );

      const walletName = pickStr(
        extra?.walletName,
        session?.link?.transport?.appName,
        session?.link?.transport?.title,
        link?.transport?.appName,
        link?.transport?.title
      );

      const permission = pickStr(extra?.permission, auth?.permission ? String(auth.permission) : "");

      const payload = {
        event: String(event || ""),
        actor: String(actorValue || ""),
        walletType,
        walletName,
        permission,
      };

      await fetch("/api/auth-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch {
      // ignore logging failures
    }
  }, [auth, session, link]);

  useEffect(() => {
    if (!actor) return;
    if (lastAuthLoggedActorRef.current === actor) return;
    lastAuthLoggedActorRef.current = actor;
    void logAuthEvent("login_success", actor, { permission: auth?.permission ? String(auth.permission) : "" });
  }, [actor, auth, logAuthEvent]);

  const connect = useCallback(async () => {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      const { link: newLink, session: newSession } = await ProtonWebSDK({
        linkOptions: { rpc: DEFAULT_RPC },
        transport: undefined, // auto-detect
      });
      setLink(newLink);
      setSession(newSession);
      const authData = newSession.auth;
      setAuth(authData);
      setNotice("Wallet connected successfully.");
      void logAuthEvent("connect_success", authData?.actor ? String(authData.actor) : "");
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      void logAuthEvent("connect_error", "", { error: err.message });
    } finally {
      setBusy(false);
      postWalletUiCleanup();
    }
  }, [logAuthEvent]);

  const disconnect = useCallback(async () => {
    setError("");
    setNotice("");
    try {
      if (link && session) await link.removeSession(session);
    } catch {
      // ignore
    }
    setLink(null);
    setSession(null);
    setAuth(null);
    setNotice("Disconnected.");
    void logAuthEvent("disconnect", "");
  }, [link, session, logAuthEvent]);

  const transact = useCallback(async (actions, onSuccess) => {
    setError("");
    setNotice("");
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      return null;
    }
    setBusy(true);
    try {
      const txResult = await session.transact({ actions }, { broadcast: true });
      setNotice("Transaction sent successfully.");
      if (onSuccess) {
        await onSuccess();
      }
      return txResult;
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
      postWalletUiCleanup();
    }
  }, [session, auth]);

  return {
    link,
    session,
    auth,
    actor,
    isContract,
    loggedIn,
    busy,
    error,
    notice,
    accessGateMsg,
    setError,
    setNotice,
    setAccessGateMsg,
    connect,
    disconnect,
    transact,
    logAuthEvent,
  };
}
